import dateFormat from "dateformat";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { getPaymentPlansQuery } from "rsl-api-106/lib/queries";
import {
  BookingStatus,
  Maybe,
  Occupant,
  Option,
  PaymentPlan,
  Query,
  QueryGetPaymentPlansArgs,
} from "rsl-api-106/lib/types";
import { SelectOptionType } from "types";

import { useQuery } from "@apollo/client";
import InputLabel from "@components/_Layout/Controls/InputLabel";
import SelectPaymentMethods from "@components/_Layout/Controls/SelectPaymentMethods";
import LoadingSpinner from "@components/_Layout/Loaders/LoadingSpinner";
import InputDiscount from "@components/Discounts/InputDiscount";
import InputGift from "@components/Discounts/InputGift";
import ProductInsurance from "@components/Products/ProductInsurance";
import ObservationBlock from "@components/Reservation/ObservationBlock";
import { Tab } from "@headlessui/react";
import { useCart, useConfig, useScrollTop, useSession } from "@hooks";
import { sortBy } from "@utils/arrays";
import { getCurrencySymbol } from "@utils/currencies";
import { optionToSelectOption } from "@utils/select";
import { sendDataLayer, trackClick } from "@utils/tracking/gtm";

import { StepTitle } from "../";

type StepPaymentProps = {
  termsChecked: boolean;
  setTermsChecked: (termsChecked: boolean) => void;
  termsError: boolean;
  setTermsError: (termsError: boolean) => void;
  paymentDueTypeSelected: string | undefined;
  setPaymentDueTypeSelected: ((paymentDueType: string) => void) | undefined;
  selectedPaymentMethod: string | undefined;
  setSelectedPaymentMethod: (paymentMethod: string) => void;
  selectedPaymentOption: string | undefined;
  setSelectedPaymentOption: (value: string) => void;
  selectedInsurance: string | undefined;
  setSelectedInsurance: (value: string | undefined) => void;
  displayInsurance?: boolean;
  showDiscounts?: boolean;
  showPaymentDue?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_payment_your_payment", //Votre paiement
  "LANG_neobooking_payment_pay_multiple_times", //Vous souhaitez régler en plusieurs fois:
  "LANG_neobooking_area_res_payment", //Paiement
  "LANG_neobooking_payment_due_date", //Date d'échéance
  "LANG_neobooking_payment_due_amount", //Montant échéance
  "LANG_neobooking_payment_amount_paid", //Montant réglé
  "LANG_neobooking_payment_amount_due", //Montant restant dû
  "LANG_neobooking_observation_remarques", //Remarques
  "LANG_neobooking_check_terms_1", //Terms & conditions
  "LANG_neobooking_check_terms_2", //Terms & conditions2
  "LANG_neobooking_check_terms_link", //Terms & conditions lien
  "LANG_neobooking_need_insurance", //Besoin d'une assurance ?
  "LANG_neobooking_add_code", //Ajouter un code
  "LANG_neobooking_add_discount_code", //Ajouter un code promo
  "LANG_neobooking_add_gift_code", //Ajouter un code cadeau
  "LANG_neobooking_acompte", //Acompte
  "LANG_neobooking_area_res_payment_er", //er
  "LANG_neobooking_area_res_payment_eme", //ème
  "LANG_neobooking_payment_pay_all", //Payer la totalité
];

const StepPayment: React.FC<StepPaymentProps> = ({
  termsChecked,
  setTermsChecked,
  termsError,
  setTermsError,
  paymentDueTypeSelected,
  setPaymentDueTypeSelected,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedPaymentOption,
  setSelectedPaymentOption,
  selectedInsurance,
  setSelectedInsurance,
  displayInsurance = true,
  showDiscounts = true,
  showPaymentDue = true,
}) => {
  /* Hooks */

  const { sessionId, getLanguageVarValue, authMode } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const {
    cart,
    updateCartPaymentsDue, //updateCartPaymentPlans
    updatePayerPaymentsDue, //updatePayerPaymentPlans
    updatePartnerPaymentsDue, //updatePartnerPaymentPlans
    updateCartPaymentMethod, //updateCartPaymentMethod
  } = useCart();
  const { data, error, loading, refetch } = useQuery<
    Query,
    QueryGetPaymentPlansArgs
  >(getPaymentPlansQuery(), {
    variables: { session: { name: sessionId!! }, input: {} },
    fetchPolicy: "no-cache",
  });
  useScrollTop();

  const [searchParams, setSearchParams] = useSearchParams();
  /* Discount State */
  const [successDiscount, setSuccessDiscount] = useState<string | null>(null);
  const [errorDiscount, setErrorDiscount] = useState<string | null>(null);

  /* Payment due types State */
  const [paymentDueTypesOptions, setPaymentDueTypesOptions] = useState<
    SelectOptionType[]
  >([]);
  const [selectedIndex, setSelectedIndex] =
    useState<number | undefined>(undefined);

  /* Loading State */
  const [loadingChangeTab, setLoadingChangeTab] = useState<boolean>(false);

  //Already init at ConfirmationPage & Reservation Page
  /*useEffect(() => {
    //Facebook Pixel Code Tracking
    ReactPixel.init(process.env.REACT_APP_GOOGLE_PIXEL_CODE!!);
  }, [])*/

  useLayoutEffect(() => {
    if (data && data.getPaymentPlans?.paymentPlans) {
      initDueTypesList(data.getPaymentPlans?.paymentPlans as Option[]);
      if (data.getPaymentPlans?.paymentPlans.length === 1) {
        setSelectedIndex(0);
        setPaymentDueTypeSelected &&
          setPaymentDueTypeSelected(
            data.getPaymentPlans?.paymentPlans?.[0]?.code!!
          );
      } else {
        const index = getSelectedIndexByPlan(
          data?.getPaymentPlans?.paymentPlans
        );
        setSelectedIndex(index ?? 0);
      }
    }
  }, [data]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        selectedPaymentOption ? parseInt(selectedPaymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );
    }
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (selectedPaymentOption) {
      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        selectedPaymentOption ? parseInt(selectedPaymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );
    }
  }, [selectedPaymentOption]);

  const initDueTypesList = (products: Option[]) => {
    const opt: SelectOptionType[] = optionToSelectOption(
      showPaymentDue
        ? products
        : products.filter(
            (x) => x.code === cart?.reservations?.[0]?.selectedPaymentDue
          )
    );
    const sortedOption = sortBy(opt, (x) => x.value.length);
    setPaymentDueTypesOptions(sortedOption);
  };

  const handleChangeTerm = () => {
    setTermsError(false);
    setTermsChecked(!termsChecked);
  };

  useEffect(() => {
    if (paymentDueTypesOptions && paymentDueTypesOptions.length > 0) {
      const sortedOption = sortBy(
        paymentDueTypesOptions,
        (x) => x.value.length
      );
      const value = sortedOption[selectedIndex ?? 0]?.value;
      setLoadingChangeTab(true);
      updatePayerPaymentsDue(value, (err) => {
        if (err) toast.error(err.message);
        setLoadingChangeTab(false);
      });
    }
  }, [paymentDueTypesOptions, selectedIndex]);

  const getOccupantWithPaymentDue = useCallback((): Occupant[] | null => {
    let occupants: Occupant[] = [];
    cart?.reservations!![0]?.stays?.forEach((x) => {
      x?.occupants?.forEach((y) => {
        if (y?.paymentDues && y.paymentDues.length > 0) {
          occupants = [...occupants, y] as Occupant[];
        }
      });
    });
    return occupants.length > 0 ? occupants : null;
  }, [cart]);

  const handleSuccessDiscount = (str: string, code?: string) => {
    setSuccessDiscount(str);
    if (code) {
      sendDataLayer({
        event: "code_promo",
        eventCategory: "ajout",
        eventAction: true, // Ex : OK ou KO, 1 ou 0, true ou false
        eventLabel: code, //123
      });
    }
  };

  const handleSuccessGift = (str: string, code?: string) => {
    setSuccessDiscount(str);
    if (code) {
      sendDataLayer({
        event: "code_fidelite",
        eventCategory: "ajout",
        eventAction: true, // Ex : OK ou KO, 1 ou 0, true ou false
        eventLabel: code, //123
      });
    }
  };

  const handleErrorDiscount = (str: string, code?: string) => {
    setErrorDiscount(str);
    if (code) {
      sendDataLayer({
        event: "code_fidelite",
        eventCategory: "ajout",
        eventAction: false, // Ex : OK ou KO, 1 ou 0, true ou false
        eventLabel: code, //123
      });
    }
  };

  const handleErrorGift = (str: string, code?: string) => {
    setErrorDiscount(str);
    if (code) {
      sendDataLayer({
        event: "code_fidelite",
        eventCategory: "ajout",
        eventAction: false, // Ex : OK ou KO, 1 ou 0, true ou false
        eventLabel: code, //123
      });
    }
  };

  const handleClickTerms = () => {
    trackClick(
      "Tunnel - Paiement",
      "click bouton",
      "Bouton - Condition de vente lecture",
      ""
    );
  };

  const getSelectedIndexByPlan = (plans: Maybe<Maybe<PaymentPlan>[]>) => {
    let index = selectedIndex ?? 0;
    const sortedOption = sortBy(plans!!, (x) => x?.code?.length!!);
    const currentPlan =
      cart?.reservations?.[0]?.selectedPaymentDue ?? undefined;
    sortedOption.forEach((x, i) => {
      if (currentPlan && x?.code === currentPlan) {
        index = i;
      }
    });
    return index;
  };

  return (
    <>
      <div>
        {displayInsurance && (
          <ProductInsurance
            selectedInsurance={selectedInsurance}
            setSelectedInsurance={setSelectedInsurance}
            title={
              <StepTitle
                className="tw-mb-4"
                subtitle={getLanguageVarValue("LANG_neobooking_need_insurance")}
              />
            }
          />
        )}
        {authMode === 2 ? (
          <div className="tw-mb-6">
            <div className="tw-titre-principal-spirit tw-mb-4">
              {getLanguageVarValue("LANG_neobooking_observation_remarques")}
            </div>
            <InputLabel truncate={false}>
              {getLanguageVarValue("LANG_neobooking_observation_intro")}
            </InputLabel>
            <ObservationBlock />
          </div>
        ) : (
          <></>
        )}
        {showDiscounts && (
          <>
            <div className="tw-titre-principal-spirit tw-mb-4">
              {getLanguageVarValue("LANG_neobooking_add_code")}
            </div>
            <div className="tw-mb-2">
              <div className="tw-p-2 tw-flex tw-flex-col tw-gap-2">
                <InputDiscount
                  onError={handleErrorDiscount}
                  onSuccess={handleSuccessDiscount}
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_add_discount_code"
                  )}
                />
                <InputGift
                  onError={handleErrorGift}
                  onSuccess={handleSuccessGift}
                  placeholder={getLanguageVarValue(
                    "LANG_neobooking_add_gift_code"
                  )}
                />
              </div>
              {(successDiscount || errorDiscount) && (
                <div className="tw-text-sm">
                  {successDiscount && (
                    <p className="tw-text-green-500">{successDiscount}</p>
                  )}
                  {errorDiscount && (
                    <p className="tw-text-red-500">{errorDiscount}</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-gap-4">
        {authMode !== 2 ? (
          <div>
            <div className="tw-titre-principal-spirit tw-mb-4">
              {getLanguageVarValue("LANG_neobooking_payment_your_payment")}
            </div>
            <Tab.Group
              selectedIndex={selectedIndex}
              onChange={setSelectedIndex}
            >
              <Tab.List
                className={
                  "tw-flex tw-justify-center tw-items-center tw-border-b-[2px] tw-border-black"
                }
              >
                {sortBy(paymentDueTypesOptions, (x) => x.value.length).map(
                  (x, i) => {
                    return (
                      <Tab
                        key={`tab_${x.value}_${i}`}
                        className={`tw-font-medium tw-py-2 tw-px-6 ${
                          selectedIndex === i
                            ? "tw-bg-black tw-text-white"
                            : "tw-bg-white tw-text-black"
                        }`}
                      >
                        {x.label}
                      </Tab>
                    );
                  }
                )}
              </Tab.List>
              <Tab.Panels>
                {sortBy(paymentDueTypesOptions, (x) => x.value.length).map(
                  (x, i) => {
                    return (
                      <Tab.Panel key={`tabpanel_${x.value}_${i}`}>
                        <>
                          <div className="tw-my-4">
                            {loadingChangeTab ? (
                              <LoadingSpinner />
                            ) : (
                              <>
                                <>
                                  {/* PAYMENT DUE - INDIV MODE */}
                                  <div className="tw-overflow-x-auto tw-mb-4">
                                    <div className="tw-overflow-hidden"></div>
                                    <table className="tw-w-full tw-text-center tw-text-sm tw-bg-white">
                                      <thead className="tw-text-black tw-font-semibold tw-tracking-wider">
                                        <tr>
                                          <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                                            {getLanguageVarValue(
                                              "LANG_neobooking_area_res_payment"
                                            )}
                                          </th>
                                          <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                                            {getLanguageVarValue(
                                              "LANG_neobooking_payment_due_date"
                                            )}
                                          </th>
                                          <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                                            {getLanguageVarValue(
                                              "LANG_neobooking_payment_due_amount"
                                            )}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="tw-bg-white">
                                        {getOccupantWithPaymentDue()?.map(
                                          (occupant, index) => {
                                            return occupant?.paymentDues?.map(
                                              (paymentDue, paymentDueIndex) => {
                                                return (
                                                  <tr
                                                    key={`tr_payment_${occupant.guiid}_${index}_${paymentDue?.amount?.amount}_${paymentDueIndex}`}
                                                  >
                                                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                      {paymentDueIndex + 1}
                                                      {paymentDueIndex + 1 === 1
                                                        ? getLanguageVarValue(
                                                            "LANG_neobooking_area_res_payment_er"
                                                          )
                                                        : getLanguageVarValue(
                                                            "LANG_neobooking_area_res_payment_eme"
                                                          )}{" "}
                                                      <span className="tw-lowercase">
                                                        {getLanguageVarValue(
                                                          "LANG_neobooking_acompte"
                                                        )}
                                                      </span>
                                                    </td>
                                                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                                      {dateFormat(
                                                        new Date(
                                                          paymentDue?.date
                                                        ),
                                                        "dd/mm/yyyy"
                                                      )}
                                                    </td>
                                                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-lg tw-font-semibold">
                                                      {paymentDue?.amount?.amount?.toFixed(
                                                        2
                                                      )}
                                                      {getCurrencySymbol(
                                                        paymentDue?.amount
                                                          ?.currencyCode!!
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                            );
                                          }
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>

                                <SelectPaymentMethods
                                  automaticPaymentOnly
                                  selected={selectedPaymentMethod}
                                  setSelected={setSelectedPaymentMethod}
                                  selectedPaymentOption={selectedPaymentOption}
                                  setSelectedPaymentOption={
                                    setSelectedPaymentOption
                                  }
                                />
                              </>
                            )}
                          </div>
                        </>
                      </Tab.Panel>
                    );
                  }
                )}
              </Tab.Panels>
            </Tab.Group>
          </div>
        ) : (
          <>
            <div className="tw-overflow-x-auto tw-mb-4">
              <div className="tw-overflow-hidden"></div>
              <table className="tw-w-full tw-text-center tw-text-sm tw-bg-white">
                <thead className="tw-text-black tw-font-semibold tw-tracking-wider">
                  <tr>
                    <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                      {getLanguageVarValue("LANG_neobooking_area_res_payment")}
                    </th>
                    <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                      {getLanguageVarValue("LANG_neobooking_payment_due_date")}
                    </th>
                    <th className="tw-px-6 tw-py-3 tw-text-center tw-uppercase">
                      {getLanguageVarValue(
                        "LANG_neobooking_payment_due_amount"
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="tw-bg-white">
                  {data?.getPaymentPlans?.paymentPlans?.map(
                    (plan, planIndex) => {
                      return plan?.partnerPaymentDues?.map(
                        (partnerPaymentDue, partnerPaymentIndex) => {
                          return (
                            <tr
                              key={`tr_payment_${plan?.code}_${planIndex}_${partnerPaymentDue?.amount?.amount}_${partnerPaymentIndex}`}
                            >
                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                {partnerPaymentIndex + 1}
                                {partnerPaymentIndex + 1 === 1
                                  ? getLanguageVarValue(
                                      "LANG_neobooking_area_res_payment_er"
                                    )
                                  : getLanguageVarValue(
                                      "LANG_neobooking_area_res_payment_eme"
                                    )}{" "}
                                <span className="tw-lowercase">
                                  {getLanguageVarValue(
                                    "LANG_neobooking_acompte"
                                  )}
                                </span>
                              </td>
                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                                {dateFormat(
                                  new Date(partnerPaymentDue?.date),
                                  "dd/mm/yyyy"
                                )}
                              </td>
                              <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-lg tw-font-semibold">
                                {partnerPaymentDue?.amount?.amount?.toFixed(2)}
                                {getCurrencySymbol(
                                  partnerPaymentDue?.amount?.currencyCode!!
                                )}
                              </td>
                            </tr>
                          );
                        }
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="tw-mb-4">
          <div className="tw-flex tw-gap-2">
            <div className="tw-self-center">
              <input
                id="reservation_terms"
                name="reservation_terms"
                type="checkbox"
                checked={termsChecked}
                onChange={handleChangeTerm}
                className={`tw-accent-v_blue-dark hover:tw-accent-v_blue-dark tw-h-4 tw-w-4 tw-mt-1 ${
                  termsError
                    ? "tw-outline tw-outline-offset-2 tw-outline-1 tw-outline-red-500"
                    : ""
                }`}
              />
            </div>
            <label
              className="tw-leading-5 tw-self-center"
              htmlFor="reservation_terms"
            >
              {getLanguageVarValue("LANG_neobooking_check_terms_1")}{" "}
              <a
                target="_blank"
                onClick={handleClickTerms}
                className="tw-underline"
                href={
                  authMode === 2
                    ? getLanguageVarValue(
                        "LANG_neobooking_check_terms_link_b2b"
                      )
                    : mode === "vacanceole"
                    ? getLanguageVarValue("LANG_neobooking_check_terms_link")
                    : getLanguageVarValue(
                        "LANG_neobooking_check_terms_link_corseole"
                      )
                }
              >
                {getLanguageVarValue("LANG_neobooking_check_terms_2")}
              </a>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

StepPayment.propTypes = {
  //
};

export default StepPayment;
